import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FormField, FormLabel } from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { RIDER_SALE_PAYMENT_METHOD_LABELS } from "./rider-sale-labels";

import type {
  RiderSaleFormValues,
  RiderSalePaymentFormValues,
} from "./rider-sale-schema";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";

interface RiderSalePaymentsSectionProps {
  form: UseFormReturn<RiderSaleFormValues>;
  paymentFields: FieldArrayWithId<RiderSaleFormValues, "payments", "id">[];
  addPayment: () => void;
  removePayment: (index: number) => void;
  methodOptionsFor: (index: number) => RiderSalePaymentFormValues["method"][];
  canAddPayment: boolean;
}

export function RiderSalePaymentsSection({
  form,
  paymentFields,
  addPayment,
  removePayment,
  methodOptionsFor,
  canAddPayment,
}: RiderSalePaymentsSectionProps) {
  return (
    <>
      <div className="flex items-center justify-between">
        <FormLabel>Pembayaran</FormLabel>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addPayment}
          disabled={!canAddPayment}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          Tambah Metode
        </Button>
      </div>

      {paymentFields.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Belum ada metode pembayaran dicatat.
        </p>
      ) : (
        <div className="space-y-2">
          {paymentFields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <FormField
                control={form.control}
                name={`payments.${index}.method`}
                render={({ field: f }) => (
                  <Select value={f.value} onValueChange={f.onChange}>
                    <SelectTrigger className="w-28 sm:w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {methodOptionsFor(index).map((m) => (
                        <SelectItem key={m} value={m}>
                          {RIDER_SALE_PAYMENT_METHOD_LABELS[m]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              <FormField
                control={form.control}
                name={`payments.${index}.amount`}
                render={({ field: f }) => (
                  <NumberInput
                    className="flex-1"
                    placeholder="0"
                    value={f.value}
                    onChange={f.onChange}
                  />
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => removePayment(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
